import { useEffect, useMemo } from 'react';

import type { AnswerFragment } from '../../present/activity-answers.graphql.generated';
import type { SuggestedAnswerFragment } from '../suggested-answers/suggested-answers.graphql.generated';
import {
  ViewProps,
  FillInBlanksSentence as FillInBlanksSentenceType,
  FillInBlanksAnswer,
  FillInBlanksResponseAnswer,
} from './fill-in-blanks.constants';
import {
  getRichtextValue,
  isRichtextValueEmpty,
} from '../shared/logic/get-richtext-value';
import { getExtensions } from 'approot/shared/richtext/richtext.utils';
import { getFillInBlanksRichtext } from './logic/get-fill-in-blanks-richtext';
import { sortFillInBlanksSentences } from './logic/sort-fill-in-blanks-sentences';
import { mergeFillInBlanksBlocks } from './logic/merge-fill-in-blanks-blocks';
import { getFillInBlanksSentenceAnswer } from './logic/get-fill-in-blanks-sentence-answer';
import { getFillInBlanksAnswerColour } from './logic/get-fill-in-blanks-answer-colour';
import { useFillInBlanksRichtextSettings } from './logic/use-fill-in-blanks-richtext-settings';
import { useFillInBlanksEditorWidth } from './logic/use-fill-in-blanks-editor-width';
import {
  FillInBlanksSentence,
  FillInBlanksSentencesWrapper,
} from './fill-in-blanks-shared.style';
import * as Styled from './fill-in-blanks-view-answers.style';
import FillInBlanksTextBlock from './fill-in-blanks-text-block';
import type { Pluck } from 'lib/gql/pluck';

const AnsweredAnswerBlock = ({
  answerBlock,
  fieldAnswer,
  fontSize,
  fontFamily,
}: {
  answerBlock: FillInBlanksAnswer;
  fieldAnswer?: FillInBlanksResponseAnswer | null;
  fontSize: string | null;
  fontFamily: string | null;
}) => {
  const colours = useMemo(
    () => getFillInBlanksAnswerColour(answerBlock),
    [answerBlock]
  );

  const settings = useFillInBlanksRichtextSettings(answerBlock);
  const { hasTextOverflowed, editorRef, setEditorRef, adjustFieldState } =
    useFillInBlanksEditorWidth({
      answer: answerBlock,
      fontSize,
      fontFamily,
    });

  const answerValue = useMemo(
    () =>
      getRichtextValue(
        getFillInBlanksRichtext(fieldAnswer),
        getExtensions(settings)
      ),
    [fieldAnswer, settings]
  );

  useEffect(() => {
    if (fieldAnswer && editorRef) {
      adjustFieldState(editorRef);
    }
  }, [editorRef, fieldAnswer, adjustFieldState]);

  return (
    <Styled.FillInBlanksField
      $colours={colours}
      $fontSize={fontSize}
      $fontFamily={fontFamily}
      $hasTextOverflowed={hasTextOverflowed}
      $minWidth={answerBlock.width}
      $punctuation={answerBlock.punctuation}
      $hasLabel={!!(answerBlock.showLabel && answerBlock.label)}
    >
      <Styled.FillInBlanksFieldInner
        $colours={colours}
        $label={answerBlock.showLabel ? answerBlock.label : null}
        $fontSize={fontSize}
        $fontFamily={fontFamily}
      >
        <Styled.FillInBlanksFieldTipTap
          $placeholder={!answerValue ? answerBlock.placeholder : null}
          $isScaffold={answerBlock.showPlaceholderAsScaffold}
        >
          <Styled.FillInBlanksFieldValue
            ref={setEditorRef}
            $fontSize={fontSize}
            $fontFamily={fontFamily}
            dangerouslySetInnerHTML={{
              __html: answerValue,
            }}
          />
        </Styled.FillInBlanksFieldTipTap>
      </Styled.FillInBlanksFieldInner>
    </Styled.FillInBlanksField>
  );
};

const UnansweredAnswerBlock = ({
  answer,
  fontSize,
  fontFamily,
}: {
  answer: FillInBlanksAnswer;
  fontSize: string | null;
  fontFamily: string | null;
}) => {
  const colours = useMemo(() => getFillInBlanksAnswerColour(answer), [answer]);

  return (
    <Styled.FillInBlanksField
      $colours={colours}
      $hasTextOverflowed={false}
      $minWidth={answer.width}
      $punctuation={answer.punctuation}
      $hasLabel={!!(answer.showLabel && answer.label)}
      $fontSize={fontSize}
      $fontFamily={fontFamily}
    >
      <Styled.FillInBlanksFieldInner
        $colours={colours}
        $label={answer.showLabel ? answer.label : null}
        $fontSize={fontSize}
        $fontFamily={fontFamily}
      >
        <Styled.FillInBlanksFieldTipTap
          $placeholder={answer.placeholder}
          $isScaffold={answer.showPlaceholderAsScaffold}
        >
          <Styled.FillInBlanksFieldValue
            className="tiptap"
            $fontSize={fontSize}
            $fontFamily={fontFamily}
          >
            <p>
              <br className="ProseMirror-trailingBreak" />
            </p>
          </Styled.FillInBlanksFieldValue>
        </Styled.FillInBlanksFieldTipTap>
      </Styled.FillInBlanksFieldInner>
    </Styled.FillInBlanksField>
  );
};

const AnswerBlocks = ({
  answerBlock,
  fieldAnswer,
  fontSize,
  fontFamily,
}: {
  answerBlock: FillInBlanksAnswer;
  fieldAnswer?: FillInBlanksResponseAnswer;
  fontSize: string | null;
  fontFamily: string | null;
}) => {
  const hasAnswer = !isRichtextValueEmpty(getFillInBlanksRichtext(fieldAnswer));

  return hasAnswer ? (
    <AnsweredAnswerBlock
      answerBlock={answerBlock}
      fieldAnswer={fieldAnswer}
      fontSize={fontSize}
      fontFamily={fontFamily}
    />
  ) : (
    <UnansweredAnswerBlock
      answer={answerBlock}
      fontSize={fontSize}
      fontFamily={fontFamily}
    />
  );
};

const Sentence = ({
  sentence,
  fieldAnswers,
  fontSize,
  fontFamily,
}: {
  sentence: FillInBlanksSentenceType;
  fieldAnswers?:
    | (AnswerFragment | Pluck<SuggestedAnswerFragment, 'suggestedAnswers'>)[]
    | null;
  fontSize: string | null;
  fontFamily: string | null;
}) => {
  const mergedBlocks = useMemo(
    () => mergeFillInBlanksBlocks(sentence),
    [sentence]
  );

  const fieldAnswerMap = useMemo(
    () => getFillInBlanksSentenceAnswer(sentence, fieldAnswers || []),
    [sentence, fieldAnswers]
  );

  return mergedBlocks.map(block => {
    return 'text' in block ? (
      <FillInBlanksTextBlock key={block.itemId} textBlock={block} />
    ) : (
      <AnswerBlocks
        key={`${block.itemId}-${block.showPlaceholderAsScaffold}-${block.placeholder}`}
        answerBlock={block}
        fieldAnswer={fieldAnswerMap[block.itemId]}
        fontSize={fontSize}
        fontFamily={fontFamily}
      />
    );
  });
};

const FillInBlanksViewAnswers = ({
  properties,
  fieldAnswers,
}: ViewProps & {
  fieldAnswers?:
    | (AnswerFragment | Pluck<SuggestedAnswerFragment, 'suggestedAnswers'>)[]
    | null;
}) => {
  const sortedSentences = useMemo(
    () => sortFillInBlanksSentences(properties.fillInBlanks.sentences),
    [properties.fillInBlanks.sentences]
  );

  return (
    <FillInBlanksSentencesWrapper
      $fontFamily={properties.fillInBlanks.fontFamily}
      $fontSize={properties.fillInBlanks.fontSize}
    >
      {sortedSentences.map(sentence => (
        <FillInBlanksSentence key={sentence.itemId}>
          <Sentence
            sentence={sentence}
            fieldAnswers={fieldAnswers}
            fontSize={properties.fillInBlanks.fontSize}
            fontFamily={properties.fillInBlanks.fontFamily}
          />
        </FillInBlanksSentence>
      ))}
    </FillInBlanksSentencesWrapper>
  );
};

export default FillInBlanksViewAnswers;
